import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import { settingSchema } from '../models/schema'
import { Setting, SettingKey } from '../models/Setting'
import { fetchSettings } from '../services/SettingService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<Setting> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'setting',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getSettingsSuccess,
  setQuery: setSettingQuery,
  reset: resetSettingState,
  resetQueryAndIds: resetSettingQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Setting }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectSettings = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedSetting = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectSettingById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectSettingsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )
export const selectSettingByKey = (key: string) =>
  createSelector([selectRawItems], (items) =>
    Object.entries(items)
      .map((i) => i[1])
      .find((setting) => setting.key === key)
  )

/**
 * Actions
 */

export const setSelectedSetting =
  (setting: Setting | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Setting>(setting)))

export const getSettings = (settings: SettingKey[]) =>
  SBAPI.FetchPaginatedDispatched(
    fetchSettings(settings),
    [settingSchema],
    slice.actions
  )
